import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { getFormattedName } from './file-system';
import { blobToDataURL } from '../download';
import Compressor from 'compressorjs';

export const isSharable = () => {
  const currentPlatform = Capacitor.getPlatform();
  if (currentPlatform === 'web') {
    return typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  } else {
    return true;
  }
};

const compressBlob = (blob, imageCompression) =>
  new Promise((resolve, reject) => {
    new Compressor(blob, {
      quality: 1,
      resize: 'contain',
      ...imageCompression,
      success(result) {
        resolve(result);
      },
      error(err) {
        reject(err);
      }
    });
  });

const normalizeImageUrls = ({ imageUrl, imageUrls }) => {
  if (Array.isArray(imageUrls) && imageUrls.length) {
    return imageUrls.filter(Boolean);
  }
  return imageUrl ? [imageUrl] : [];
};

export const share = async ({ title, text, url, imageUrl, imageUrls, fileNames, imageCompression }) => {
  const currentPlatform = Capacitor.getPlatform();
  const shareData = { title, text, url };
  const urls = normalizeImageUrls({ imageUrl, imageUrls });
  const names = Array.isArray(fileNames) ? fileNames : [];

  // A Capacitor WebView may expose navigator.share, but it cannot reliably
  // hand its blob URLs to native apps. Native uses the Capacitor Share plugin
  // below with real cache-file URIs instead.
  // Browser desktop/mobile web uses Web Share. A Capacitor WebView reports an
  // iOS/Android platform, so native apps continue below with cache-file URIs.
  if (currentPlatform === 'web' && navigator && !!navigator.share) {
    if (urls.length) {
      const files = [];
      for (let index = 0; index < urls.length; index += 1) {
        const res = await fetch(urls[index], { cache: 'no-cache' });
        const blob = await res.blob();
        const compressedBlob = await compressBlob(blob, imageCompression);
        const filename = names[index] || (urls.length > 1 ? `thumbnail-${index + 1}.png` : 'thumbnail.png');
        files.push(new File([compressedBlob], filename, { type: compressedBlob.type }));
      }
      shareData.files = files;
    }
    return navigator.share(shareData);
  }

  if (currentPlatform === 'ios' || currentPlatform === 'android') {
    if (urls.length) {
      const fileUris = [];
      for (let index = 0; index < urls.length; index += 1) {
        const res = await fetch(urls[index], { cache: 'no-cache' });
        const blob = await res.blob();
        const compressedBlob = await compressBlob(blob, imageCompression);
        const dataUrl = await blobToDataURL(compressedBlob);
        // Capacitor expects just base64 data. Writing the data-URL prefix into
        // the cache file corrupts the image handed to the native share sheet.
        const base64Data = dataUrl.slice(dataUrl.indexOf(',') + 1);
        const preferredName = names[index] || `${new Date().getTime()}${urls.length > 1 ? `-${index + 1}` : ''}.png`;
        const formattedName = await getFormattedName(preferredName, '', Directory.Cache);
        const writeResult = await Filesystem.writeFile({
          path: formattedName,
          data: base64Data,
          directory: Directory.Cache
        });
        fileUris.push(writeResult.uri);
      }

      // `url` is treated as text/a link by native share targets. Supplying
      // every export as a file works for both a one-page and multi-page pack.
      shareData.files = fileUris;
      delete shareData.url;

      // WhatsApp on Android treats a combined text + stream intent as a text
      // share and drops the images. Studio exports are self-contained PNGs,
      // so send native image exports as files only on both platforms.
      if (currentPlatform === 'ios' || currentPlatform === 'android') {
        delete shareData.title;
        delete shareData.text;
      }
    }

    return Share.share(shareData);
  }
};
